/**
 * @format
 * @module ui/visibility-by-identity
 */
import { getUserRole, isOnDemand } from "lib/identity/api";
import { getEntitlementClasses } from "lib/identity/entitlements";
import { qsa } from "utils/utils";

/**
 * @method init
 * @description Reveals elements that have been hidden via the "u-hidden" class,
 * when they have a specific class that corresponds to the current user's role
 * and/or entitlement(s).
 * @param {String} [parentSelector] - optional parent element CSS selector
 */
export default function init(parentSelector) {
  let elements = [];
  elements = elements.concat(getRoleElements(parentSelector, "role"));
  elements = elements.concat(getEntitlementElements(parentSelector));
  elements = elements.concat(getOnDemandElements(parentSelector));

  // In case no user object is received from the analytics endpoint,
  // we would have no roles or entitlements to assign, and both
  // getRoleElements and getEntitlementElements would return empty arrays.
  // So we default to an anonymous user, and reveal the corresponding buttons.
  if (elements.length === 0) {
    elements = qsa(".js-role-visibility--anonymous");
  }

  elements.forEach(e => e.classList.remove("u-hidden"));
}

/**
 * @method getOnDemandElements
 * @description Get all the on-demand elements from the DOM, based on JS class names.
 * Use this for features that you want to reveal by user role, but never for EA users.
 * @param {String} [parentSelector] - optional parent element CSS selector
 * @example Valid classes:
 *   js-ondemand-visibility--anonymous
 *   js-ondemand-visibility--registered
 *   js-ondemand-visibility--subscribed
 * @returns {Array} of HTMLElements
 */
function getOnDemandElements(parentSelector) {
  // Don't continue if user has enterprise access
  return isOnDemand("enterprise") ? [] : getRoleElements(parentSelector, "ondemand");
}

/**
 * @method getEntitlementElements
 * @description Get all the entitlement elements from the DOM, based on JS class names.
 * @param {String} [parentSelector] - optional parent element CSS selector
 * @example Valid classes:
 *   js-entitlement-visibility--advisor
 * @returns {Array} of HTMLElements
 */
function getEntitlementElements(parentSelector) {
  let elements = [];
  getEntitlementClasses().forEach(className => {
    let selector = `.js-entitlement-visibility--${className}`;
    if (parentSelector) {
      selector = `${parentSelector} ${selector}`;
    }
    elements = elements.concat(qsa(selector));
  });
  return elements;
}

/**
 * @method getRoleElements
 * @description Get all the role elements from the DOM, based on JS class names.
 * @param {String} [parentSelector] - optional parent element CSS selector
 * @param {"role" | "ondemand"} type - for CSS selector
 * @example Valid classes:
 *   js-role-visibility--anonymous
 *   js-role-visibility--registered
 *   js-role-visibility--subscribed
 * @returns {Array} of HTMLElements
 */
function getRoleElements(parentSelector, type) {
  let elements = [];
  let selector = null;
  const role = getUserRole();
  if (role && role.name) {
    selector = `.js-${type}-visibility--${role.name.toLowerCase()}`;
  }
  if (selector) {
    if (parentSelector) {
      selector = `${parentSelector} ${selector}`;
    }
    elements = qsa(selector);
  }
  return elements;
}
